import type { FileSystemTree } from '@webcontainer/api';

/**
 * Inlined "jira-mock-like" demo project. This mirrors the architecture the
 * app-factory pipeline produces — webpack with a `@forge/bridge` -> jira-mock
 * alias, a `view.theme.enable()` + ReactDOM.render() entry, a React app that
 * calls `requestJira` + `invoke` against the mocked bridge.
 *
 * Atlaskit is intentionally excluded so npm install stays manageable (~60-90s
 * cold). The point of this POC is to prove the toolchain works end to end —
 * webpack alias resolves, ts-loader compiles, jira-mock intercepts bridge
 * calls, React renders. Atlaskit can be layered on once this passes.
 */

const FILE_PACKAGE_JSON = JSON.stringify(
  {
    name: 'jira-mock-demo',
    private: true,
    version: '0.0.1',
    scripts: {
      start: 'webpack serve --config webpack.demo.config.js --mode development',
      build: 'webpack --config webpack.demo.config.js --mode production',
    },
    dependencies: {
      react: '^18.3.1',
      'react-dom': '^18.3.1',
      // Atlaskit — small representative subset to validate the bundler can
      // handle ADS components, their CSS-in-JS runtime, and their transitive
      // dep graph inside WebContainer.
      '@atlaskit/css-reset': 'latest',
      '@atlaskit/button': 'latest',
      '@atlaskit/lozenge': 'latest',
      '@atlaskit/page-header': 'latest',
    },
    devDependencies: {
      '@types/react': '^18.3.3',
      '@types/react-dom': '^18.3.0',
      'html-webpack-plugin': '^5.6.0',
      'ts-loader': '^9.5.1',
      typescript: '^5.4.5',
      webpack: '^5.94.0',
      'webpack-cli': '^5.1.4',
      'webpack-dev-server': '^5.0.4',
      'style-loader': '^4.0.0',
      'css-loader': '^7.1.2',
      // Node polyfills. Atlaskit transitive deps + jira-mock-style packages
      // commonly reference `process.env.NODE_ENV`, `Buffer`, and a few stream
      // helpers. We mirror what jira-mock-setup's SKILL.md prescribes.
      buffer: '^6.0.3',
      process: '^0.11.10',
      'crypto-browserify': '^3.12.0',
      'stream-browserify': '^3.0.0',
    },
  },
  null,
  2,
);

const FILE_TSCONFIG = JSON.stringify(
  {
    compilerOptions: {
      target: 'ES2020',
      module: 'ESNext',
      moduleResolution: 'node',
      jsx: 'react-jsx',
      strict: true,
      esModuleInterop: true,
      skipLibCheck: true,
      forceConsistentCasingInFileNames: true,
      allowSyntheticDefaultImports: true,
      resolveJsonModule: true,
      isolatedModules: true,
    },
    include: ['src', 'packages/jira-mock/src'],
  },
  null,
  2,
);

const FILE_WEBPACK_CONFIG = `const path = require('path');
const webpack = require('webpack');
const HtmlWebpackPlugin = require('html-webpack-plugin');

// Mirrors what jira-mock-setup emits: webpack.demo.config.js with a single
// @forge/bridge -> jira-mock alias, ts-loader, an HTML template, Node
// polyfill fallbacks for Atlaskit's transitive deps, and a dev server.
const JIRA_MOCK_SRC = path.resolve(__dirname, 'packages/jira-mock/src');

module.exports = {
  entry: { Demo: './src/demo/DemoEntry.tsx' },

  output: {
    path: path.resolve(__dirname, 'build-demo'),
    filename: '[name]/app.js',
    clean: true,
    publicPath: 'auto',
  },

  resolve: {
    extensions: ['.ts', '.tsx', '.js', '.jsx'],
    alias: {
      // Drop-in replacement for @forge/bridge — every \`import … from
      // '@forge/bridge'\` is routed to the local mock.
      '@forge/bridge': path.resolve(JIRA_MOCK_SRC, 'bridge.ts'),
    },
    fallback: {
      buffer: require.resolve('buffer'),
      process: require.resolve('process/browser'),
      crypto: require.resolve('crypto-browserify'),
      stream: require.resolve('stream-browserify'),
      fs: false,
      path: false,
    },
  },

  module: {
    rules: [
      {
        test: /\\.tsx?$/,
        use: [{
          loader: 'ts-loader',
          options: {
            // jira-mock ships .ts files, not a compiled package — ts-loader
            // must compile them, so don't exclude node_modules and allow ts
            // inside.
            allowTsInNodeModules: true,
            transpileOnly: true,
          },
        }],
      },
      { test: /\\.css$/, use: ['style-loader', 'css-loader'] },
    ],
  },

  plugins: [
    new webpack.ProvidePlugin({
      process: 'process/browser',
      Buffer: ['buffer', 'Buffer'],
    }),
    new HtmlWebpackPlugin({
      filename: 'Demo/index.html',
      inject: true,
      chunks: ['Demo'],
      templateContent: \`<!doctype html>
<html>
  <head><meta charset="utf-8"><title>jira-mock demo</title></head>
  <body><div id="root"></div></body>
</html>\`,
    }),
  ],

  devServer: {
    static: path.resolve(__dirname, 'build-demo'),
    port: 3111,
    host: '0.0.0.0',
    hot: false,
    historyApiFallback: { index: '/Demo/index.html' },
    client: { logging: 'warn', overlay: false },
  },
};
`;

const FILE_DEMO_ENTRY = `import React from 'react';
import { createRoot } from 'react-dom/client';
import { view } from '@forge/bridge';
import '@atlaskit/css-reset';
import { App } from '../App';
import '../styles.css';

// Mirrors jira-mock-setup's DemoEntry pattern: \`view.theme.enable()\` first
// (a no-op in jira-mock, but the real Forge platform requires it), then
// mount the same component tree the real Forge entry would.
void view.theme.enable().then(() => {
  const root = createRoot(document.getElementById('root')!);
  root.render(<App />);
});
`;

const FILE_APP = `import React, { useEffect, useState } from 'react';
import { requestJira, invoke, view } from '@forge/bridge';
import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import PageHeader from '@atlaskit/page-header';

interface Issue {
  key: string;
  fields: { summary: string; status: { name: string } };
}

function statusAppearance(name: string) {
  if (/done|closed/i.test(name)) return 'success';
  if (/progress/i.test(name)) return 'inprogress';
  return 'default';
}

export function App() {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [me, setMe] = useState<string>('…');
  const [comments, setComments] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // Through the @forge/bridge alias, both of these calls hit jira-mock.
        const myselfRes = await requestJira({ url: '/rest/api/3/myself' });
        const myself = await myselfRes.json();
        setMe(myself.displayName);

        const searchRes = await requestJira({
          url: '/rest/api/3/search/jql?jql=project=DEMO',
        });
        const search = await searchRes.json();
        setIssues(search.issues);
      } catch (e: any) {
        setErr(e?.message ?? String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function addComment(issueKey: string) {
    const res = await invoke('addComment', { issueKey, body: 'Looks good!' });
    setComments((c) => ({ ...c, [issueKey]: (c[issueKey] ?? 0) + 1 }));
    console.log('addComment ->', res);
  }

  if (err) return <div className="card error">Error: {err}</div>;
  if (loading) return <div className="card">Loading…</div>;

  return (
    <div className="page">
      <PageHeader>jira-mock demo</PageHeader>
      <div className="who">signed in as {me}</div>

      <div className="banner">
        This whole React app is being served by webpack-dev-server running
        inside a WebContainer in your browser tab. Every requestJira / invoke
        call below is intercepted by jira-mock via the @forge/bridge alias.
      </div>

      <ul className="issues">
        {issues.map((i) => (
          <li key={i.key} className="issue">
            <div className="issue-main">
              <span className="issue-key">{i.key}</span>
              <span className="issue-summary">{i.fields.summary}</span>
            </div>
            <div className="issue-meta">
              <Lozenge appearance={statusAppearance(i.fields.status.name) as any}>
                {i.fields.status.name}
              </Lozenge>
              <Button appearance="default" onClick={() => addComment(i.key)}>
                + comment ({comments[i.key] ?? 0})
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <footer className="ftr">
        bridge build id: <code>{view.constructor.name || 'jira-mock'}</code>
      </footer>
    </div>
  );
}
`;

const FILE_STYLES = `body { background: #f4f5f7; }
.page { max-width: 880px; margin: 0 auto; padding: 24px; }
.who { color: #5e6c84; font-size: 12px; margin-top: -8px; }
.banner { background: #deebff; color: #0747a6; border-radius: 6px; padding: 10px 14px; margin: 16px 0 20px; font-size: 13px; }
.issues { list-style: none; padding: 0; margin: 0; background: white; border-radius: 6px; box-shadow: 0 1px 1px rgba(9,30,66,.13); }
.issue { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-bottom: 1px solid #ebecf0; gap: 12px; }
.issue:last-child { border-bottom: 0; }
.issue-key { font-weight: 600; color: #0052cc; margin-right: 12px; }
.issue-meta { display: flex; align-items: center; gap: 10px; }
.ftr { margin-top: 20px; color: #97a0af; font-size: 11px; text-align: right; }
.card { padding: 24px; background: white; border-radius: 6px; max-width: 600px; margin: 24px auto; }
.card.error { color: #bf2600; border: 1px solid #ff8f73; }
`;

// ── jira-mock package source (the "drop-in replacement" for @forge/bridge) ──

const FILE_BRIDGE_TS = `// Inlined jira-mock: the bridge.ts module the webpack alias points at.
// Every API surface @forge/bridge exposes that the demo touches goes here.

import { handle as handleRequestJira } from './requestJira';

export const view = {
  theme: {
    // In real Forge this enables the Atlaskit theme. In the mock it's a no-op
    // that resolves immediately so the entry point's chained code runs.
    enable: () => Promise.resolve(),
  },
  getContext: () =>
    Promise.resolve({
      extension: { project: { key: 'DEMO' }, issue: { key: 'DEMO-1' } },
      cloudId: 'mock-cloud',
    }),
};

export function requestJira(opts: { url: string; method?: string; body?: any }) {
  return handleRequestJira(opts);
}

export async function invoke(functionKey: string, payload: any) {
  // The real bridge dispatches to a Forge resolver. The mock returns a canned
  // response so the demo can show end-to-end interaction without a backend.
  console.log('[jira-mock] invoke', functionKey, payload);
  if (functionKey === 'addComment') {
    return { ok: true, commentId: 'cmt-' + Math.random().toString(36).slice(2, 8) };
  }
  return { ok: true };
}

export const router = {
  open: (href: string) => {
    console.log('[jira-mock] router.open', href);
  },
};
`;

const FILE_REQUEST_JIRA = `// Inlined jira-mock: routes requestJira(...) calls to canned mock data.
import { MOCK_MYSELF, MOCK_ISSUES } from './data';

interface Opts { url: string; method?: string; body?: any }

function makeResponse(payload: any, status = 200) {
  return {
    ok: status >= 200 && status < 300,
    status,
    json: async () => payload,
    text: async () => JSON.stringify(payload),
  } as any;
}

export async function handle({ url, method = 'GET' }: Opts) {
  // Trim host + querystring for matching.
  const path = url.split('?')[0];

  if (method === 'GET' && path === '/rest/api/3/myself') {
    return makeResponse(MOCK_MYSELF);
  }

  if (method === 'GET' && path === '/rest/api/3/search/jql') {
    return makeResponse({ issues: MOCK_ISSUES, total: MOCK_ISSUES.length });
  }

  const issueMatch = path.match(/^\\/rest\\/api\\/3\\/issue\\/([^/]+)$/);
  if (method === 'GET' && issueMatch) {
    const issue = MOCK_ISSUES.find((i) => i.key === issueMatch[1]);
    return issue
      ? makeResponse(issue)
      : makeResponse({ errorMessages: ['Issue not found'] }, 404);
  }

  console.warn('[jira-mock] Unhandled URL', method, path);
  return makeResponse({ errorMessages: ['Unhandled mock URL: ' + path] }, 404);
}
`;

const FILE_MOCK_DATA = `// Inlined jira-mock: data fixtures for the demo.
export const MOCK_MYSELF = {
  accountId: '5b10ac8d82e05b22cc7d4ef5',
  displayName: 'Demo User',
  emailAddress: 'demo@example.com',
};

export const MOCK_ISSUES = [
  {
    key: 'DEMO-1',
    fields: {
      summary: 'Hook up the Marketplace listing template',
      status: { name: 'In Progress' },
    },
  },
  {
    key: 'DEMO-2',
    fields: {
      summary: 'Capture screenshots for 1840×900 hero',
      status: { name: 'To Do' },
    },
  },
  {
    key: 'DEMO-3',
    fields: {
      summary: 'Wire build-error log back into generate skill',
      status: { name: 'To Do' },
    },
  },
  {
    key: 'DEMO-4',
    fields: {
      summary: 'Run the demo inside WebContainer',
      status: { name: 'Done' },
    },
  },
];
`;

export const demoFiles: FileSystemTree = {
  'package.json': { file: { contents: FILE_PACKAGE_JSON } },
  'tsconfig.json': { file: { contents: FILE_TSCONFIG } },
  'webpack.demo.config.js': { file: { contents: FILE_WEBPACK_CONFIG } },
  src: {
    directory: {
      'App.tsx': { file: { contents: FILE_APP } },
      'styles.css': { file: { contents: FILE_STYLES } },
      demo: {
        directory: {
          'DemoEntry.tsx': { file: { contents: FILE_DEMO_ENTRY } },
        },
      },
    },
  },
  packages: {
    directory: {
      'jira-mock': {
        directory: {
          src: {
            directory: {
              'bridge.ts': { file: { contents: FILE_BRIDGE_TS } },
              'requestJira.ts': { file: { contents: FILE_REQUEST_JIRA } },
              'data.ts': { file: { contents: FILE_MOCK_DATA } },
            },
          },
        },
      },
    },
  },
};
